import {weakMemo} from '@packages/utilities/memo';

/**
 * The source for uploaded fonts
 */
export const FONT_SOURCE_CUSTOM = 'Custom' as const;

/**
 * The source for default designer fonts served by Google Fonts
 */
export const FONT_SOURCE_GOOGLE = 'Google' as const;

/**
 * The source for fonts added through the Google Fonts integration
 */
export const FONT_SOURCE_GOOGLE_CUSTOM = 'Google-Custom' as const;

/**
 * The source for fonts added through the Adobe integration
 */
export const FONT_SOURCE_TYPEKIT = 'Typekit' as const;

/**
 * The source for web-safe fonts
 */
export const FONT_SOURCE_WEBSAFE = 'Websafe' as const;

const SANS_SERIF = 'sans-serif';
const SERIF = 'serif';
const CURSIVE = 'cursive';
const MONOSPACE = 'monospace';

export type FontAxis = {
  /*
   * 4-character keyword.
   * Registered axes: "wght" | "wdth" | "slnt" | "ital" | "opsz"
   * Custom axes: any keyword in all caps, e.g. "GRAD" | "XTRA" | "YTFI"
   */
  tag: string;
  /** Full name of axis */
  name: string;
  /** Min value for axis range */
  min: number;
  /** Min value for axis range */
  max: number;
  /** Default value for axis */
  default: number;
  /** Description for axis */
  description?: string;
};

export type FontAxes = Array<FontAxis>;

export type FontAxesMap = {
  [tag: string]: FontAxis;
};

export type Font = {
  family: string;
  source:
    | typeof FONT_SOURCE_CUSTOM
    | typeof FONT_SOURCE_GOOGLE
    | typeof FONT_SOURCE_GOOGLE_CUSTOM
    | typeof FONT_SOURCE_TYPEKIT
    | typeof FONT_SOURCE_WEBSAFE;
  type: 'sans-serif' | 'serif' | 'cursive' | 'monospace';
  weights: Array<number>;
  italic: 'same' | Array<number>;
  fallback: Array<string>;
  axes?: FontAxes;
  // maybe for google fonts only?
  subsets?: Array<string>;
  origin?: [string, string];
};

export const DEFAULT_FONTS: Array<Font> = [
  {
    family: 'system-ui',
    source: FONT_SOURCE_WEBSAFE,
    type: SANS_SERIF,
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    italic: 'same',
    fallback: [
      '-apple-system',
      'BlinkMacSystemFont',
      'Segoe UI',
      'Roboto',
      'Oxygen',
      'Ubuntu',
      'Cantarell',
      'Fira Sans',
      'Droid Sans',
      'Helvetica Neue',
    ],
  },
  {
    family: 'Arial',
    source: FONT_SOURCE_WEBSAFE,
    type: SANS_SERIF,
    weights: [400, 700],
    italic: 'same',
    fallback: ['Helvetica Neue', 'Helvetica'],
  },
  {
    family: 'Bitter',
    source: FONT_SOURCE_GOOGLE,
    type: SERIF,
    weights: [400, 700],
    italic: [400],
    fallback: [],
  },
  {
    family: 'Changa One',
    source: FONT_SOURCE_GOOGLE,
    type: SANS_SERIF,
    weights: [400],
    italic: 'same',
    fallback: ['Impact'],
  },
  {
    family: 'Droid Sans',
    source: FONT_SOURCE_GOOGLE,
    type: SANS_SERIF,
    weights: [400, 700],
    italic: [],
    fallback: [],
  },
  {
    family: 'Droid Serif',
    source: FONT_SOURCE_GOOGLE,
    type: SERIF,
    weights: [400, 700],
    italic: 'same',
    fallback: [],
  },
  {
    family: 'Exo',
    source: FONT_SOURCE_GOOGLE,
    type: SANS_SERIF,
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    italic: 'same',
    fallback: [],
  },
  {
    family: 'Georgia',
    source: FONT_SOURCE_WEBSAFE,
    type: SERIF,
    weights: [400, 700],
    italic: 'same',
    fallback: ['Times', 'Times New Roman'],
  },
  {
    family: 'Great Vibes',
    source: FONT_SOURCE_GOOGLE,
    type: CURSIVE,
    weights: [400],
    italic: [],
    fallback: [],
  },
  {
    family: 'Impact',
    source: FONT_SOURCE_WEBSAFE,
    type: SANS_SERIF,
    weights: [700],
    italic: 'same',
    fallback: ['Haettenschweiler', 'Franklin Gothic Bold', 'Charcoal'],
  },
  {
    family: 'Inconsolata',
    source: FONT_SOURCE_GOOGLE,
    type: MONOSPACE,
    weights: [400, 700],
    italic: [],
    fallback: [],
  },
  {
    family: 'Lato',
    source: FONT_SOURCE_GOOGLE,
    type: SANS_SERIF,
    weights: [100, 300, 400, 700, 900],
    italic: 'same',
    fallback: [],
  },
  {
    family: 'Merriweather',
    source: FONT_SOURCE_GOOGLE,
    type: SERIF,
    weights: [300, 400, 700, 900],
    italic: 'same',
    fallback: [],
  },
  {
    family: 'Montserrat',
    source: FONT_SOURCE_GOOGLE,
    type: SANS_SERIF,
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    italic: 'same',
    fallback: [],
  },
  {
    family: 'Open Sans',
    source: FONT_SOURCE_GOOGLE,
    type: SANS_SERIF,
    weights: [300, 400, 600, 700, 800],
    italic: 'same',
    fallback: [],
  },
  {
    family: 'Oswald',
    source: FONT_SOURCE_GOOGLE,
    type: SANS_SERIF,
    weights: [200, 300, 400, 500, 600, 700],
    italic: [],
    fallback: [],
  },
  {
    family: 'Palatino Linotype',
    source: FONT_SOURCE_WEBSAFE,
    type: SERIF,
    weights: [400, 700],
    italic: 'same',
    fallback: ['Book Antiqua', 'Palatino'],
  },
  {
    family: 'PT Sans',
    source: FONT_SOURCE_GOOGLE,
    type: SANS_SERIF,
    weights: [400, 700],
    italic: 'same',
    fallback: [],
  },
  {
    family: 'PT Serif',
    source: FONT_SOURCE_GOOGLE,
    type: SERIF,
    weights: [400, 700],
    italic: 'same',
    fallback: [],
  },
  {
    family: 'Tahoma',
    source: FONT_SOURCE_WEBSAFE,
    type: SANS_SERIF,
    weights: [400, 700],
    italic: 'same',
    fallback: ['Verdana', 'Segoe'],
  },
  {
    family: 'Times New Roman',
    source: FONT_SOURCE_WEBSAFE,
    type: SERIF,
    weights: [400, 700],
    italic: 'same',
    fallback: ['TimesNewRoman', 'Times', 'Baskerville', 'Georgia'],
  },
  {
    family: 'Trebuchet MS',
    source: FONT_SOURCE_WEBSAFE,
    type: SANS_SERIF,
    weights: [400, 700],
    italic: 'same',
    fallback: ['Lucida Grande', 'Lucida Sans Unicode', 'Lucida Sans', 'Tahoma'],
  },
  {
    family: 'Ubuntu',
    source: FONT_SOURCE_GOOGLE,
    type: SANS_SERIF,
    weights: [300, 400, 500, 700],
    italic: 'same',
    fallback: ['Helvetica'],
  },
  {
    family: 'Varela',
    source: FONT_SOURCE_GOOGLE,
    type: SANS_SERIF,
    weights: [400],
    italic: [],
    fallback: [],
  },
  {
    family: 'Varela Round',
    source: FONT_SOURCE_GOOGLE,
    type: SANS_SERIF,
    weights: [400],
    italic: [],
    fallback: [],
  },
  {
    family: 'Verdana',
    source: FONT_SOURCE_WEBSAFE,
    type: SANS_SERIF,
    weights: [400, 700],
    italic: 'same',
    fallback: ['Geneva'],
  },
  {
    family: 'Vollkorn',
    source: FONT_SOURCE_GOOGLE,
    type: SERIF,
    weights: [400, 700],
    italic: 'same',
    fallback: [],
  },
];

/**
 * The tag for the "Italic" registed axis
 * https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Fonts/Variable_Fonts_Guide#italic
 */
export const REGISTERED_AXIS_ITAL = 'ital' as const;

/**
 * The tag for the "Optical size" registed axis
 * https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Fonts/Variable_Fonts_Guide#optical_size
 */
export const REGISTERED_AXIS_OPSZ = 'opsz' as const;

/**
 * The tag for the "Slant" registed axis
 * https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Fonts/Variable_Fonts_Guide#slant
 */
export const REGISTERED_AXIS_SLNT = 'slnt' as const;

/**
 * The tag for the "Width" registed axis
 * https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Fonts/Variable_Fonts_Guide#width
 */
export const REGISTERED_AXIS_WDTH = 'wdth' as const;

/**
 * The tag for the "Weight" registed axis
 * https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Fonts/Variable_Fonts_Guide#weight
 */
export const REGISTERED_AXIS_WGHT = 'wght' as const;

export const AXIS_ITAL_DEFAULTS = {
  tag: REGISTERED_AXIS_ITAL,
  name: 'Italic',
  min: 0,
  max: 1,
  step: 1,
  default: 0,
  description:
    'Adjust the style from roman to italic. This can be provided as a continuous range within a single font file, like most axes, or as a toggle between two roman and italic files that form a family as a pair.',
} as const;

export const AXIS_OPSZ_DEFAULTS = {
  tag: REGISTERED_AXIS_OPSZ,
  name: 'Optical size',
  min: 6,
  max: 144,
  step: 0.1,
  default: 14,
  description:
    'Adapt the style to specific text sizes. At smaller sizes, letters typically become optimized for more legibility. At larger sizes, optimized for headlines, with more extreme weights and widths. In CSS this axis is activated automatically when it is available.',
} as const;

export const AXIS_SLNT_DEFAULTS = {
  tag: REGISTERED_AXIS_SLNT,
  name: 'Slant',
  min: -90,
  max: 90,
  step: 1,
  default: 0,
  description:
    "Adjust the style from upright to slanted, also known to typographers as an 'oblique' style. Rarely, slant can work in the other direction, called a 'backslanted' or 'reverse oblique' style.",
} as const;

export const AXIS_WGHT_DEFAULTS = {
  tag: REGISTERED_AXIS_WGHT,
  name: 'Weight',
  min: 1,
  max: 1000,
  step: 1,
  default: 400,
  description:
    'Adjust the style from lighter to bolder in typographic color, by varying stroke weights, spacing and kerning, and other aspects of the type. This typically changes overall width, and so may be used in conjunction with Width and Grade axes.',
} as const;

export const AXIS_WDTH_DEFAULTS = {
  tag: REGISTERED_AXIS_WDTH,
  name: 'Width',
  min: 25,
  max: 200,
  step: 0.1,
  default: 100,
  description:
    'Adjust the style from narrower to wider, by varying the proportions of counters, strokes, spacing and kerning, and other aspects of the type. This typically changes the typographic color in a subtle way, and so may be used in conjunction with Width and Grade axes.',
} as const;

/**
 * Default axes if there are no default step/ranges for an axes
 * from https://fonts.google.com/variablefonts#axis-definitions.
 * This list was generated from the following script that was run
 * in the browser on that page:
 *
 * ```js
 * const axes = Array.from(document.querySelectorAll('.mat-text--title.gmat-subtitle-1.axis__display-name')).reduce((acc, e) => {
 *   const [tag, name, range, description] = e.parentNode.children;
 *
 *   acc[tag.innerHTML] = {
 *     tag: tag.innerHTML,
 *     name: name.innerHTML,
 *     min: Number(range.children[1].innerHTML.split(' ')[1]),
 *     max: Number(range.children[2].innerHTML.split(' ')[1]),
 *     step: Number(range.children[3].innerHTML.split(' ')[1]),
 *     default: Number(range.children[0].innerHTML.split(' ')[1]),
 *     description: description.innerHTML
 *   }
 *   return acc;
 * }, {})
 * console.info({axes});
 * ```
 */
export const DEFAULT_AXES: {
  [tag: string]:
    | undefined
    | (FontAxis & {
        step: number;
      });
} = {
  // Registered axes — see https://w3c.github.io/csswg-drafts/css-fonts/#font-variation-settings-def
  [REGISTERED_AXIS_ITAL]: AXIS_ITAL_DEFAULTS,
  [REGISTERED_AXIS_OPSZ]: AXIS_OPSZ_DEFAULTS,
  [REGISTERED_AXIS_SLNT]: AXIS_SLNT_DEFAULTS,
  [REGISTERED_AXIS_WGHT]: AXIS_WGHT_DEFAULTS,
  [REGISTERED_AXIS_WDTH]: AXIS_WDTH_DEFAULTS,

  // Custom axes
  YTAS: {
    tag: 'YTAS',
    name: 'Ascender Height',
    min: 0,
    max: 1000,
    step: 1,
    default: 750,
    description:
      'A parametric axis for varying the height of lowercase ascenders.',
  },
  CASL: {
    tag: 'CASL',
    name: 'Casual',
    min: 0,
    max: 1,
    step: 0.01,
    default: 0,
    description:
      'Adjust stroke curvature, contrast, and terminals from a sturdy, rational Linear style to a friendly, energetic Casual style.',
  },
  XTRA: {
    tag: 'XTRA',
    name: 'Counter Width',
    min: -1000,
    max: 2000,
    step: 1,
    default: 400,
    description:
      'A parametric axis for varying counter widths in the X dimension.',
  },
  CRSV: {
    tag: 'CRSV',
    name: 'Cursive',
    min: 0,
    max: 1,
    step: 0.1,
    default: 0.5,
    description:
      "Control the substitution of cursive forms along the Slant axis. 'Off' (0) maintains Roman letterforms such as a double-storey a and g, 'Auto' (0.5) allows for Cursive substitution, and 'On' (1) asserts cursive forms even in upright text with a Slant of 0.",
  },
  YTDE: {
    tag: 'YTDE',
    name: 'Descender Depth',
    min: -1000,
    max: 0,
    step: 1,
    default: -250,
    description:
      'A parametric axis for varying the depth of lowercase descenders.',
  },
  EHLT: {
    tag: 'EHLT',
    name: 'Edge Highlight',
    min: 0,
    max: 1000,
    step: 1,
    default: 12,
    description: 'Controls thickness of edge highlight details.',
  },
  ELGR: {
    tag: 'ELGR',
    name: 'Element Grid',
    min: 1,
    max: 2,
    step: 0.1,
    default: 1,
    description:
      'In modular fonts, where glyphs are composed using multiple copies of the same element, this axis controls how many elements are used per one grid unit.',
  },
  ELSH: {
    tag: 'ELSH',
    name: 'Element Shape',
    min: 0,
    max: 100,
    step: 0.1,
    default: 0,
    description:
      'In modular fonts, where glyphs are composed using multiple copies of the same element, this axis controls the shape of the element',
  },
  EDPT: {
    tag: 'EDPT',
    name: 'Extrusion Depth',
    min: 0,
    max: 1000,
    step: 1,
    default: 100,
    description: 'Controls the 3D depth on contours.',
  },
  YTFI: {
    tag: 'YTFI',
    name: 'Figure Height',
    min: -1000,
    max: 2000,
    step: 1,
    default: 600,
    description: 'A parametric axis for varying the height of figures.',
  },
  FILL: {
    tag: 'FILL',
    name: 'Fill',
    min: 0,
    max: 1,
    step: 0.01,
    default: 0,
    description:
      'Fill in transparent forms with opaque ones. Sometimes interior opaque forms become transparent, to maintain contrasting shapes. This can be useful in animation or interaction to convey a state transition. Ranges from 0 (no treatment) to 1 (completely filled).',
  },
  FLAR: {
    tag: 'FLAR',
    name: 'Flair',
    min: 0,
    max: 100,
    step: 0.1,
    default: 0,
    description:
      'As the flair axis grows, the straight sans serif terminals develop a swelling, and joints become more idiosyncratic. Originally referring to the flaring of the stems, Flair stands for style and panache.',
  },
  GRAD: {
    tag: 'GRAD',
    name: 'Grade',
    min: -1000,
    max: 1000,
    step: 1,
    default: 0,
    description:
      'Finesse the style from lighter to bolder in typographic color, without any changes overall width, line breaks or page layout. Negative grade makes the style lighter, while positive grade makes it bolder. The units are the same as in the Weight axis.',
  },
  HEXP: {
    tag: 'HEXP',
    name: 'Hyper Expansion',
    min: 0,
    max: 100,
    step: 0.1,
    default: 0,
    description: 'Expansion of inner and outer space of glyphs.',
  },
  YTLC: {
    tag: 'YTLC',
    name: 'Lowercase Height',
    min: 0,
    max: 1000,
    step: 1,
    default: 500,
    description: 'A parametric axis for varying the height of the lowercase.',
  },
  MONO: {
    tag: 'MONO',
    name: 'Monospace',
    min: 0,
    max: 1,
    step: 0.01,
    default: 0,
    description:
      'Adjust the style from Proportional (natural widths, default) to Monospace (fixed width). With proportional spacing, each glyph takes up a unique amount of space on a line, while monospace is when all glyphs have the same total character width.',
  },
  XROT: {
    tag: 'XROT',
    name: 'Rotation in X',
    min: -180,
    max: 180,
    step: 1,
    default: 0,
    description: 'Letters rotate around the X axis.',
  },
  YROT: {
    tag: 'YROT',
    name: 'Rotation in Y',
    min: -180,
    max: 180,
    step: 1,
    default: 0,
    description: 'Letters rotate around the Y axis.',
  },
  ROND: {
    tag: 'ROND',
    name: 'Roundness',
    min: 0,
    max: 100,
    step: 1,
    default: 0,
    description:
      'Adjust shapes from angular defaults (0%) to become increasingly rounded (up to 100%).',
  },
  SOFT: {
    tag: 'SOFT',
    name: 'Softness',
    min: 0,
    max: 100,
    step: 0.1,
    default: 0,
    description: 'Adjust letterforms to become more and more soft and rounded.',
  },
  XOPQ: {
    tag: 'XOPQ',
    name: 'Thick Stroke',
    min: -1000,
    max: 2000,
    step: 1,
    default: 88,
    description:
      'A parametric axis for varying thick stroke weights, such as stems.',
  },
  YOPQ: {
    tag: 'YOPQ',
    name: 'Thin Stroke',
    min: -1000,
    max: 2000,
    step: 1,
    default: 116,
    description:
      'A parametric axis for varying thin stroke weights, such as bars and hairlines.',
  },
  YTUC: {
    tag: 'YTUC',
    name: 'Uppercase Height',
    min: 0,
    max: 1000,
    step: 1,
    default: 725,
    description:
      'A parametric axis for varying the heights of uppercase letterforms.',
  },
  WONK: {
    tag: 'WONK',
    name: 'Wonky',
    min: 0,
    max: 1,
    step: 1,
    default: 0,
    description:
      "Toggle the subsitution of wonky forms. 'Off' (0) maintains more conventional letterforms, while 'On' (1) maintains wonky letterforms, such as leaning stems in roman, or flagged ascenders in italic. These forms are also controlled by Optical Size.",
  },
  YEAR: {
    tag: 'YEAR',
    name: 'Year',
    min: -4000,
    max: 4000,
    step: 1,
    default: 2000,
    description:
      'Axis that shows in a metaphoric way the effect of time on a chosen topic.',
  },
};

export type AvailableAxes = Set<string>;

/**
 * Gets a list of the available axes across all the available fonts.
 * This includes the default list of axes we hard-code, as well
 * as axes that are available to specific fonts. If you're interested in
 * getting the available axes for a particular font, this is not what you're
 * looking for. Please access the 'font.axes' property directly instead.
 */
export const getAllAvailableVariableFontAxes = weakMemo(
  function getAllAvailableVariableFontAxes(allFonts: Font[]): AvailableAxes {
    const defaultAxes = Object.keys(DEFAULT_AXES);
    const combinedAxes: AvailableAxes = new Set(defaultAxes);

    allFonts.forEach(({axes}) => {
      if (axes) {
        axes.forEach(({tag}) => {
          combinedAxes.add(tag);
        });
      }
    });

    return combinedAxes;
  }
);

export function getAxisStep(tag: string): number {
  // FontAxesMap doesn't have `step` because the opentype library
  // currently doesn't provide us that information when a user uploads
  // a custom font, so we base it off a "known" value from the google fonts website.
  return DEFAULT_AXES[tag]?.step ?? 1;
}

export function getAxisRange(
  tag: string,
  axesMap: FontAxesMap
): null | {
  min: number;
  max: number;
  default: number;
} {
  const axis = axesMap[tag] ?? DEFAULT_AXES[tag];

  return axis
    ? {
        min: axis.min,
        max: axis.max,
        default: axis.default,
      }
    : null;
}

/**
 * Gets the name of an axis (e.g. "Weight") given an axis tag (e.g. "wght").
 *
 * There is an "axis.name" property that gets set as a part of the opentype font
 * parsing. We don't use that property directly. Some fonts provide proper names
 * for an axis like "Weight". While others use the axis tag as the name. This can
 * lead to the label for an axis property item changing when you switch fonts.
 *
 * To standardize what name is shown for an axis, we'll map the tag to an axis in
 * the default list of axes and use the name property from that axis. If a tag
 * can't be mapped to anything in the list, the name will fall back to the tag itself.
 */
export function getAxisName(tag: string): string {
  const axis = DEFAULT_AXES[tag];

  // If the tag maps to an axis from the default list of axes
  // we'll return the axis name (e.g. "Weight")
  if (axis) {
    return axis.name;
  }

  // If the tag doesn't map to an axis, we'll fall
  // back to the tag name itself (e.g. "wght")
  return tag;
}

// https://w3c.github.io/csswg-drafts/css-fonts/#font-variation-settings-def
// must be 4 ASCII characters and cannot be outside the U+20–7E codepoint range.
const tagRegex = /^[\w]{4}$/;

export function isValidAxisTag(tag: string): boolean {
  return tagRegex.test(tag);
}

export function isRegisteredAxis(tag: string): boolean {
  return (
    tag === REGISTERED_AXIS_WGHT ||
    tag === REGISTERED_AXIS_WDTH ||
    tag === REGISTERED_AXIS_SLNT ||
    tag === REGISTERED_AXIS_ITAL ||
    tag === REGISTERED_AXIS_OPSZ
  );
}
